// OfferLetter.js
import React, { useState, useEffect, useRef } from 'react';
import './OfferLetter.css';
import { toast } from 'react-toastify';
import {
  FiFileText,
  FiEdit,
  FiHome,
  FiBriefcase,
  FiClock,
  Fisun,
  FiCalendar,
  FiDollarSign,
  FiClipboard,
  FiSettings,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiSend,
  FiMail,
  FiTrash2,
  FiSave,
  FiX,
  FiAlertTriangle,
  FiCheck,
  FiDownload,
  FiUpload,
  Fifix
} from "react-icons/fi";
 
const OfferLetter = ({ application, onClose, onSave, user, API_BASE_URL }) => {
  // Default Templates (initial state)
  const [templates, setTemplates] = useState({
    workingHours: [
      { id: 1, name: 'Standard (9-6)', value: '9:00 AM to 6:00 PM IST, Monday through Friday', default: true },
      { id: 2, name: 'Flexible Hours', value: 'Flexible working hours between 8:00 AM to 8:00 PM', default: false },
      { id: 3, name: 'Shift-based', value: 'Shift timing as per roster schedule', default: false },
      { id: 4, name: 'Hybrid Model', value: '3 days work from office, 2 days work from home', default: false }
    ],
    saturdayHours: [
      { id: 1, name: '2nd & 4th Sat Off', value: '9:00 AM to 2:00 PM (1st and 3rd Saturdays)', default: true },
      { id: 2, name: '5 Day Week', value: 'Monday to Friday (Saturdays off)', default: false },
      { id: 3, name: 'Alternate Saturdays', value: 'Alternate Saturdays working - 9:00 AM to 1:00 PM', default: false }
    ],
    leavePolicy: {
      sickLeave: [
        { id: 1, name: 'Standard', value: '6 days/year', default: true },
        { id: 2, name: 'Enhanced', value: '12 days/year', default: false },
        { id: 3, name: 'Flexible', value: 'As per company policy', default: false }
      ],
      earnedLeave: [
        { id: 1, name: 'Standard', value: '12 days/year', default: true },
        { id: 2, name: 'Accrual', value: '1.25 days per month', default: false },
        { id: 3, name: 'Enhanced', value: '18 days/year', default: false }
      ],
      casualLeave: [
        { id: 1, name: 'Standard', value: '6 days/year', default: true },
        { id: 2, name: 'Flexi', value: '8 days/year', default: false }
      ],
      maternityLeave: [
        { id: 1, name: 'Statutory', value: '26 weeks as per Maternity Benefit Act', default: true },
        { id: 2, name: 'Enhanced', value: '6 months fully paid maternity leave', default: false }
      ],
      paternityLeave: [
        { id: 1, name: 'Standard', value: '5 days', default: true },
        { id: 2, name: 'Enhanced', value: '15 days', default: false }
      ]
    },
    benefits: {
      pfContribution: [
        { id: 1, name: 'Statutory', value: '12% of basic salary', default: true },
        { id: 2, name: 'Voluntary', value: 'Voluntary PF contribution available', default: false }
      ],
      esiContribution: [
        { id: 1, name: 'Applicable', value: 'Applicable if gross salary ≤ ₹21,000', default: true },
        { id: 2, name: 'Not Applicable', value: 'Not applicable', default: false }
      ],
      medicalCoverage: [
        { id: 1, name: 'Group Mediclaim', value: 'Group Mediclaim coverage as per company policy', default: true },
        { id: 2, name: 'Family Coverage', value: 'Family medical coverage included', default: false }
      ]
    }
  });

  // Default Terms and Conditions Templates
  const [termsTemplates, setTermsTemplates] = useState([
    {
      id: 1,
      name: 'Standard Terms',
      content: `1. You must uphold the organisation's values and maintain high standards of ethics, punctuality, discipline, and integrity
2. You are expected to maintain strict confidentiality regarding all company data and client information
3. Any software, design, or product created during your tenure shall be the exclusive property of the company
4. This offer is contingent upon satisfactory completion of background verification
5. You must comply with all company policies including POSH policy and IT asset usage policy
6. During probation period, notice period shall be 1 month or salary in lieu thereof
7. Salary revision, if any, will be based on your performance and company policy
8. You shall not engage in any other employment or business without company's written consent
9. Your appointment is subject to medical fitness
10. Company reserves the right to modify terms based on business requirements`,
      default: true
    },
    {
      id: 2,
      name: 'IT Company Terms',
      content: `1. All intellectual property developed during employment belongs to the company
2. Non-compete clause valid for 6 months post employment
3. Mandatory compliance with information security policies
4. Regular working hours with flexibility for project needs
5. Performance-based annual bonus
6. Stock options as per company policy
7. Continuous learning and certification support
8. Remote work policy as applicable
9. Equipment provided by company must be returned upon separation
10. Data protection and privacy compliance mandatory`,
      default: false
    },
    {
      id: 3,
      name: 'Startup Terms',
      content: `1. Flexible working hours with goal-oriented approach
2. ESOP participation as per vesting schedule
3. Flat hierarchy with direct access to leadership
4. Fast-paced environment with dynamic role requirements
5. Quarterly performance reviews
6. Learning budget for skill development
7. Remote-first culture with periodic team gatherings
8. Transparency in company performance and metrics
9. Opportunity to wear multiple hats and grow rapidly
10. Startup equity participation subject to board approval`,
      default: false
    }
  ]);

  // Company Data
  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyLogo: '',
    registeredOffice: '',
    corporateOffice: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    cinNumber: '',
    companyPAN: '',
    gstNumber: '',
    companyType: 'Private Limited',
    authorizedSignatory: '',
    signatoryDesignation: 'HR Manager',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    panNumber: '',
    uanNumber: '',
    pfNumber: '',
    pfCode: '',
    esiCode: '',
    lwfCode: '',
    contactPerson: '',
    hrEmail: '',
    hrManagerName: '',
    designation: 'HR Manager',
    appointmentLetterNumber: '',
    employeeCodePrefix: 'EMP',
    statutoryRegistrations: 'PF, ESI, Professional Tax',
    medicalPolicy: 'Group Mediclaim coverage as per company policy',
    insuranceCoverage: 'Group Personal Accident Insurance ₹5,00,000',
    companyStamp: '',
    hrDepartment: 'Human Resources Department',
    offerValidityDays: 15,
    probationExtensionPolicy: 'May be extended at company discretion',
    documentRequirements: '1. Educational Certificates\n2. Experience Letters\n3. ID Proof\n4. Address Proof\n5. Photographs',
    backgroundCheckPolicy: 'Offer is subject to satisfactory background verification'
  });

  // Offer Data with dynamic fields
  const [offerData, setOfferData] = useState({
    candidateName: application.name,
    candidateEmail: application.email,
    position: application.jobTitle,
    department: application.department,
    joiningDate: '',
    salary: '',
    ctc: '',
    basicSalary: '',
    hra: '',
    specialAllowance: '',
    conveyanceAllowance: '',
    medicalAllowance: '',
    otherAllowances: '',
    grossSalary: '',
    netSalary: '',
    annualBonus: '',
    performanceBonus: '',
    workLocation: '',
    reportingManager: '',
    employeeCode: '',
    probationPeriod: '6 months',
    noticePeriod: '2 months',
    offerReference: `HR/IN/${new Date().getFullYear()}/${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    offerDate: new Date().toISOString().split('T')[0],
    offerExpiryDate: '',
    workingHours: '',
    saturdayHours: '',
    leavePolicy: {
      sickLeave: '',
      earnedLeave: '',
      casualLeave: '',
      publicHolidays: 'As per the calendar',
      maternityLeave: '',
      paternityLeave: ''
    },
    benefits: {
      pfContribution: '',
      esiContribution: '',
      gratuity: 'Applicable after 5 years of continuous service',
      medicalCoverage: '',
      insurance: 'Group Personal Accident Insurance',
      trainingPrograms: 'Technical and soft skills training',
      mealCoupons: 'As per company policy',
      transportAllowance: 'If applicable'
    },
    termsAndConditions: '',
    additionalTerms: '',
    selectedTemplate: 'Standard Terms'
  });

  // Template Management State
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showTermsManager, setShowTermsManager] = useState(false);
  const [activeTemplateSection, setActiveTemplateSection] = useState('workingHours');
  const [newTemplate, setNewTemplate] = useState({ name: '', value: '' });
  const [customTerms, setCustomTerms] = useState('');
  
  // Other State Variables
  const [isSending, setIsSending] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [companyStampPreview, setCompanyStampPreview] = useState('');
  
  // HR Signature State
  const [hrSignature, setHrSignature] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const signatureCanvasRef = useRef(null);

  // Load templates from backend
  const loadTemplatesFromBackend = async () => {
    try {
      const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
      
      // Try to load templates from backend
      const response = await fetch(`${API_BASE_URL}/api/offer-letters/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Merge backend templates with defaults
          setTemplates(prev => ({
            ...prev,
            workingHours: [...prev.workingHours, ...(data.data.workingHours || [])],
            saturdayHours: [...prev.saturdayHours, ...(data.data.saturdayHours || [])],
            leavePolicy: {
              ...prev.leavePolicy,
              sickLeave: [...prev.leavePolicy.sickLeave, ...(data.data.leavePolicy?.sickLeave || [])],
              earnedLeave: [...prev.leavePolicy.earnedLeave, ...(data.data.leavePolicy?.earnedLeave || [])],
              casualLeave: [...prev.leavePolicy.casualLeave, ...(data.data.leavePolicy?.casualLeave || [])],
              maternityLeave: [...prev.leavePolicy.maternityLeave, ...(data.data.leavePolicy?.maternityLeave || [])],
              paternityLeave: [...prev.leavePolicy.paternityLeave, ...(data.data.leavePolicy?.paternityLeave || [])]
            },
            benefits: {
              ...prev.benefits,
              pfContribution: [...prev.benefits.pfContribution, ...(data.data.benefits?.pfContribution || [])],
              esiContribution: [...prev.benefits.esiContribution, ...(data.data.benefits?.esiContribution || [])],
              medicalCoverage: [...prev.benefits.medicalCoverage, ...(data.data.benefits?.medicalCoverage || [])]
            }
          }));
          
          if (data.data.termsTemplates && data.data.termsTemplates.length > 0) {
            setTermsTemplates(prev => [...prev, ...data.data.termsTemplates]);
          }
        }
      }
    } catch (error) {
      console.log('Using default templates, backend connection failed:', error.message);
      // Continue with default templates if backend fails
    }
  };

  // Save template to backend
  const saveTemplateToBackend = async (templateData) => {
    try {
      const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/offer-letters/templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateData)
      });
      
      return response.ok;
    } catch (error) {
      console.error('Error saving template to backend:', error);
      return false;
    }
  };

  // Get user data from token
  const getUserDataFromToken = () => {
    try {
      const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
      const userEmail = user?.email || '';
      
      const emailDomain = userEmail.split('@')[1];
      const companyNameFromEmail = emailDomain ? emailDomain.split('.')[0].toUpperCase() : 'Your Company';
      
      return {
        name: user?.name || 'HR Manager',
        email: userEmail || 'hr@company.com',
        company: user?.company || companyNameFromEmail
      };
    } catch (error) {
      return {
        name: 'HR Manager',
        email: 'hr@company.com',
        company: 'Your Company'
      };
    }
  };

  // Initialize data with default templates
  useEffect(() => {
    const userData = getUserDataFromToken();
    const today = new Date();
    const expiryDate = new Date();
    expiryDate.setDate(today.getDate() + 15);
    
    const empCode = `${companyData.employeeCodePrefix || 'EMP'}${today.getFullYear()}${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;
    
    setCompanyData(prev => ({
      ...prev,
      companyName: prev.companyName || userData.company,
      companyEmail: prev.companyEmail || userData.email,
      hrManagerName: prev.hrManagerName || userData.name,
      hrEmail: prev.hrEmail || userData.email,
      contactPerson: prev.contactPerson || userData.name,
      authorizedSignatory: prev.authorizedSignatory || userData.name,
      appointmentLetterNumber: prev.appointmentLetterNumber || `AL/${today.getFullYear()}/${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`
    }));
    
    // Set default templates
    const defaultWorkingHours = templates.workingHours.find(t => t.default);
    const defaultSaturdayHours = templates.saturdayHours.find(t => t.default);
    const defaultTerms = termsTemplates.find(t => t.default);
    
    const defaultLeavePolicy = {};
    Object.keys(templates.leavePolicy).forEach(key => {
      const defaultItem = templates.leavePolicy[key].find(t => t.default);
      if (defaultItem) {
        defaultLeavePolicy[key] = defaultItem.value;
      }
    });
    
    const defaultBenefits = {};
    Object.keys(templates.benefits).forEach(key => {
      const defaultItem = templates.benefits[key].find(t => t.default);
      if (defaultItem) {
        defaultBenefits[key] = defaultItem.value;
      }
    });
    
    setOfferData(prev => ({
      ...prev,
      offerDate: today.toISOString().split('T')[0],
      offerExpiryDate: expiryDate.toISOString().split('T')[0],
      employeeCode: empCode,
      workingHours: defaultWorkingHours?.value || '',
      saturdayHours: defaultSaturdayHours?.value || '',
      leavePolicy: { ...prev.leavePolicy, ...defaultLeavePolicy },
      benefits: { ...prev.benefits, ...defaultBenefits },
      termsAndConditions: defaultTerms?.content || '',
      selectedTemplate: defaultTerms?.name || 'Standard Terms'
    }));
    
    // Load templates from backend
    loadTemplatesFromBackend();
    
  }, []);

  // Template Management Functions
  const handleTemplateSelect = (section, subSection, template) => {
    if (subSection) {
      // For nested sections like leavePolicy.sickLeave
      setOfferData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subSection]: template.value
        }
      }));
    } else {
      // For direct sections like workingHours
      setOfferData(prev => ({
        ...prev,
        [section]: template.value
      }));
    }
  };

  const handleTermsTemplateSelect = (template) => {
    setOfferData(prev => ({
      ...prev,
      termsAndConditions: template.content,
      selectedTemplate: template.name
    }));
  };

  const handleCustomTermsChange = (value) => {
    setCustomTerms(value);
    setOfferData(prev => ({
      ...prev,
      termsAndConditions: value,
      selectedTemplate: 'Custom'
    }));
  };

  const addNewTemplate = async () => {
    if (!newTemplate.name || !newTemplate.value) {
      toast.error('Please enter both template name and value');
      return;
    }

    const newId = Date.now();
    const newTemplateItem = {
      id: newId,
      name: newTemplate.name,
      value: newTemplate.value,
      default: false
    };

    // Save to backend
    const templateData = {
      category: activeTemplateSection.includes('.') 
        ? activeTemplateSection.split('.')[0] 
        : activeTemplateSection,
      type: activeTemplateSection.includes('.') 
        ? activeTemplateSection.split('.')[1] 
        : activeTemplateSection,
      name: newTemplate.name,
      value: newTemplate.value
    };

    const saved = await saveTemplateToBackend(templateData);
    
    if (saved) {
      // Update local state
      setTemplates(prev => {
        const newTemplates = { ...prev };
        
        if (activeTemplateSection.includes('.')) {
          const [parent, child] = activeTemplateSection.split('.');
          newTemplates[parent] = {
            ...newTemplates[parent],
            [child]: [...newTemplates[parent][child], newTemplateItem]
          };
        } else {
          newTemplates[activeTemplateSection] = [
            ...newTemplates[activeTemplateSection],
            newTemplateItem
          ];
        }
        
        return newTemplates;
      });

      setNewTemplate({ name: '', value: '' });
      toast.success('Template added successfully!');
    } else {
      alert('Failed to save template to server. Please try again.');
    }
  };

  const addNewTermsTemplate = async () => {
    if (!customTerms.trim()) {
      alert('Please enter terms content');
      return;
    }

    const templateName = prompt('Enter template name:');
    if (!templateName) return;

    const newTemplateItem = {
      id: Date.now(),
      name: templateName,
      content: customTerms,
      default: false
    };

    // Save to backend
    const templateData = {
      category: 'terms',
      type: 'terms_conditions',
      name: templateName,
      value: customTerms
    };

    const saved = await saveTemplateToBackend(templateData);
    
    if (saved) {
      setTermsTemplates(prev => [...prev, newTemplateItem]);
      setCustomTerms('');
      alert('Terms template saved!');
    } else {
      alert('Failed to save template to server. Please try again.');
    }
  };

  const deleteTemplate = (section, templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    setTemplates(prev => {
      const newTemplates = { ...prev };
      
      if (section.includes('.')) {
        const [parent, child] = section.split('.');
        newTemplates[parent] = {
          ...newTemplates[parent],
          [child]: newTemplates[parent][child].filter(t => t.id !== templateId)
        };
      } else {
        newTemplates[section] = newTemplates[section].filter(t => t.id !== templateId);
      }
      
      return newTemplates;
    });
  };

  const deleteTermsTemplate = (templateId) => {
    if (!window.confirm('Are you sure you want to delete this terms template?')) return;
    setTermsTemplates(prev => prev.filter(t => t.id !== templateId));
  };

  const setAsDefaultTemplate = (section, templateId) => {
    setTemplates(prev => {
      const newTemplates = { ...prev };
      
      if (section.includes('.')) {
        const [parent, child] = section.split('.');
        newTemplates[parent] = {
          ...newTemplates[parent],
          [child]: newTemplates[parent][child].map(t => ({
            ...t,
            default: t.id === templateId
          }))
        };
      } else {
        newTemplates[section] = newTemplates[section].map(t => ({
          ...t,
          default: t.id === templateId
        }));
      }
      
      return newTemplates;
    });
  };

  const setAsDefaultTermsTemplate = (templateId) => {
    setTermsTemplates(prev => 
      prev.map(t => ({
        ...t,
        default: t.id === templateId
      }))
    );
  };

  // HR Digital Signature Functions
  const openSignaturePad = () => {
    setShowSignatureModal(true);
    setTimeout(() => {
      const canvas = signatureCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }, 100);
  };

  const handleSaveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const signatureData = canvas.toDataURL('image/png');
      setHrSignature(signatureData);
      setShowSignatureModal(false);
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleCanvasMouseDown = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    canvas.isDrawing = true;
  };

  const handleCanvasMouseMove = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !canvas.isDrawing) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handleCanvasMouseUp = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      canvas.isDrawing = false;
    }
  };

  useEffect(() => {
    if (showSignatureModal && signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      canvas.width = 400;
      canvas.height = 150;
      
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [showSignatureModal]);

  // Handle company input changes
  const handleCompanyInputChange = (field, value) => {
    setCompanyData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle offer data changes
  const handleInputChange = (field, value) => {
    setOfferData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (field === 'salary') {
      const monthlySalary = parseInt(value.replace(/[^0-9]/g, '')) || 0;
      const annualCTC = monthlySalary * 12;
      setOfferData(prev => ({
        ...prev,
        ctc: `₹${annualCTC.toLocaleString('en-IN')} per annum`
      }));
    }
  };

  const handleLeavePolicyChange = (field, value) => {
    setOfferData(prev => ({
      ...prev,
      leavePolicy: {
        ...prev.leavePolicy,
        [field]: value
      }
    }));
  };

  const handleBenefitsChange = (field, value) => {
    setOfferData(prev => ({
      ...prev,
      benefits: {
        ...prev.benefits,
        [field]: value
      }
    }));
  };

  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPEG, PNG)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Please upload an image smaller than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      setLogoPreview(base64Image);
      setCompanyData(prev => ({
        ...prev,
        companyLogo: base64Image
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle company stamp upload
  const handleStampUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (JPEG, PNG)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      setCompanyStampPreview(base64Image);
      setCompanyData(prev => ({
        ...prev,
        companyStamp: base64Image
      }));
    };
    reader.readAsDataURL(file);
  };

  // Template Selector Component
  const TemplateSelector = ({ label, value, section, subSection, options }) => (
    <div className="template-selector">
      <label>{label}</label>
      <div className="template-dropdown">
        <select
          value={value}
          onChange={(e) => {
            const selected = options.find(opt => opt.value === e.target.value);
            if (selected) {
              handleTemplateSelect(section, subSection, selected);
            }
          }}
        >
          <option value="">Select {label}</option>
          {options.map(option => (
            <option key={option.id} value={option.value}>
              {option.name}
            </option>
          ))}
        </select>
        <button
          className="edit-template-btn"
          onClick={() => {
            setActiveTemplateSection(subSection ? `${section}.${subSection}` : section);
            setShowTemplateManager(true);
          }}
          title="Manage Templates"
        >
          <FiSettings />
        </button>
      </div>
      <div className="template-preview">
        {value ? `Selected: ${options.find(opt => opt.value === value)?.name || 'Custom'}` : 'No template selected'}
      </div>
    </div>
  );

  // Calculate salary breakup
  const calculateSalaryBreakup = () => {
    if (!offerData.salary) return;
    
    const monthlySalary = parseInt(offerData.salary.replace(/[^0-9]/g, '')) || 0;
    const basic = Math.round(monthlySalary * 0.5);
    const hra = Math.round(monthlySalary * 0.4);
    const specialAllowance = monthlySalary - basic - hra;
    const conveyance = 1600;
    const medical = 1250;
    
    setOfferData(prev => ({
      ...prev,
      basicSalary: `₹${basic.toLocaleString('en-IN')}`,
      hra: `₹${hra.toLocaleString('en-IN')}`,
      specialAllowance: `₹${specialAllowance.toLocaleString('en-IN')}`,
      conveyanceAllowance: `₹${conveyance.toLocaleString('en-IN')}`,
      medicalAllowance: `₹${medical.toLocaleString('en-IN')}`,
      grossSalary: `₹${monthlySalary.toLocaleString('en-IN')}`
    }));
  };

  // Generate PDF Preview
  const generatePDF = () => {
    if (isGeneratingPDF) return;
    
    setIsGeneratingPDF(true);
    
    setTimeout(() => {
      try {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('Please allow popups to generate PDF');
          setIsGeneratingPDF(false);
          return;
        }

        const currentDate = new Date();
        const offerDate = new Date(offerData.joiningDate || currentDate);
        
        const day = offerDate.getDate();
        const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
        const formattedDateWithSuffix = `${day}${suffix} ${offerDate.toLocaleDateString('en-IN', {
          month: 'long',
          year: 'numeric'
        })}`;

        const pdfContent = generatePDFContent(formattedDateWithSuffix);
        
        printWindow.document.write(pdfContent);
        printWindow.document.close();
        
        setTimeout(() => {
          printWindow.print();
          setTimeout(() => {
            printWindow.close();
            setIsGeneratingPDF(false);
          }, 1000);
        }, 500);
        
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF preview');
        setIsGeneratingPDF(false);
      }
    }, 100);
  };

  // Enhanced PDF Generation with dynamic content
  const generatePDFContent = (formattedDateWithSuffix) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Appointment Letter - ${offerData.candidateName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Raleway', 'Arial', sans-serif; 
            margin: 0;
            padding: 40px; 
            line-height: 1.6; 
            color: #1e2330;
            font-size: 12px;
            background: #ffffff;
          }
          
          .page-wrapper {
            max-width: 800px;
            margin: 0 auto;
            background: #ffffff;
            position: relative;
            border: 1px solid #e5e7eb;
            padding: 20px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          
          /* Header Section */
          .header { 
            background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
            padding: 30px 25px;
            margin: -20px -20px 30px -20px;
            color: #ffffff;
            position: relative;
            overflow: hidden;
          }
          
          .header-content {
            position: relative;
            z-index: 1;
          }
          
          .header-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 20px;
          }
          
          .company-logo {
            max-width: 100px;
            max-height: 100px;
            background: #ffffff;
            padding: 10px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          }
          
          .company-info {
            flex: 1;
            text-align: center;
            margin: 0 20px;
          }
          
          .company-name {
            font-size: 24px;
            font-weight: 800;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .company-details {
            font-size: 10px;
            line-height: 1.6;
            opacity: 0.95;
          }
          
          /* Title Section */
          .offer-title { 
            font-size: 22px; 
            margin: 25px 0; 
            color: #2c5aa0;
            text-align: center;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 2px;
            padding: 15px;
            background: linear-gradient(90deg, transparent 0%, rgba(44, 90, 160, 0.05) 50%, transparent 100%);
            border-top: 2px solid #2c5aa0;
            border-bottom: 2px solid #2c5aa0;
          }
          
          /* Reference Box */
          .reference-box {
            background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
            border: 2px solid #e5e7eb;
            border-left: 4px solid #2c5aa0;
            border-radius: 8px;
            padding: 15px 20px;
            margin-bottom: 25px;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 10px;
          }
          
          .reference-item {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }
          
          .reference-label {
            font-size: 9px;
            color: #6b7280;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .reference-value {
            font-size: 11px;
            color: #1e2330;
            font-weight: 700;
          }
          
          /* Content Sections */
          .content {
            padding: 0 10px;
          }
          
          .section { 
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          
          .section-title { 
            font-weight: 800; 
            margin-bottom: 12px; 
            color: #2c5aa0;
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding-bottom: 8px;
            border-bottom: 2px solid #2c5aa0;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .section-number {
            width: 28px;
            height: 28px;
            background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
            color: #ffffff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 800;
            flex-shrink: 0;
          }
          
          /* Address Section */
          .address-box {
            background: #f9fafb;
            border-left: 4px solid #2c5aa0;
            padding: 15px;
            border-radius: 4px;
            margin: 15px 0;
          }
          
          .address-box strong {
            color: #2c5aa0;
            display: block;
            margin-bottom: 5px;
          }
          
          /* Tables */
          .compensation-table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            font-size: 11px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            border-radius: 8px;
            overflow: hidden;
          }
          
          .compensation-table tr:first-child td {
            background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
            color: #ffffff;
            font-weight: 700;
          }
          
          .compensation-table td {
            padding: 12px 15px;
            border: 1px solid #e5e7eb;
          }
          
          .compensation-table td:first-child {
            font-weight: 700;
            width: 40%;
            background: #f9fafb;
            color: #1e2330;
          }
          
          .compensation-table tr:first-child td:first-child {
            background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
            color: #ffffff;
          }
          
          /* Important Notice Box */
          .candidate-signature-note {
            background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
            padding: 20px;
            border: 2px solid #ffd700;
            border-left: 6px solid #f59e0b;
            border-radius: 8px;
            margin: 25px 0;
            box-shadow: 0 4px 12px rgba(245, 158, 11, 0.15);
          }
          
          .candidate-signature-note strong {
            color: #92400e;
            font-size: 13px;
            display: block;
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          /* Signature Section */
          .signature-area { 
            margin-top: 50px;
            padding-top: 30px;
            border-top: 3px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            gap: 40px;
            page-break-inside: avoid;
          }
          
          .signature-box {
            flex: 1;
            text-align: center;
            background: #f9fafb;
            padding: 25px 20px;
            border-radius: 8px;
            border: 2px solid #e5e7eb;
          }
          
          .signature-image {
            max-width: 180px;
            max-height: 70px;
            border: 2px solid #e5e7eb;
            border-radius: 4px;
            margin: 15px auto;
            display: block;
            background: #ffffff;
            padding: 5px;
          }
          
          .signature-line { 
            border-top: 2px solid #1e2330; 
            width: 80%; 
            margin: 40px auto 15px; 
          }
          
          .digital-stamp {
            color: #10b981;
            font-size: 9px;
            font-weight: 700;
            margin: 10px 0;
            background: #d1fae5;
            padding: 5px 10px;
            border-radius: 4px;
            display: inline-block;
          }
          
          .signature-details {
            font-size: 10px;
            line-height: 1.8;
            margin-top: 15px;
          }
          
          .signature-details strong {
            color: #1e2330;
            font-size: 11px;
          }
          
          /* Footer */
          .document-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: center;
            font-size: 9px;
            color: #6b7280;
          }
          
          /* Print Styles */
          @media print {
            body {
              margin: 0;
              padding: 20px;
            }
            
            .page-wrapper {
              box-shadow: none;
            }
            
            .section {
              page-break-inside: avoid;
            }
            
            .signature-area {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="page-wrapper">
          <div class="header">
            <div class="header-content">
              <div class="header-top">
                ${logoPreview ? `
                  <div>
                    <img src="${logoPreview}" alt="Company Logo" class="company-logo">
                  </div>
                ` : ''}
                <div class="company-info">
                  <div class="company-name">${companyData.companyName || 'Our Company'}</div>
                  <div class="company-details">
                    ${companyData.registeredOffice || '[Registered Office Address]'}<br>
                    CIN: ${companyData.cinNumber || '[CIN Number]'} | PAN: ${companyData.companyPAN || '[PAN]'} | GST: ${companyData.gstNumber || '[GST]'}<br>
                    Email: ${companyData.companyEmail || '[Email]'} | Phone: ${companyData.companyPhone || '[Phone]'}<br>
                    Website: ${companyData.companyWebsite || '[Website]'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="offer-title">LETTER OF APPOINTMENT</div>

          <div class="reference-box">
            <div class="reference-item">
              <span class="reference-label">Reference Number</span>
              <span class="reference-value">${offerData.offerReference}</span>
            </div>
            <div class="reference-item">
              <span class="reference-label">Date of Issue</span>
              <span class="reference-value">${new Date().toLocaleDateString('en-IN')}</span>
            </div>
            <div class="reference-item">
              <span class="reference-label">Appointment Letter No</span>
              <span class="reference-value">${companyData.appointmentLetterNumber}</span>
            </div>
          </div>

          <div class="content">
            <div class="section">
              <div class="address-box">
                <strong>To,</strong><br>
                ${offerData.candidateName}<br>
                ${application.address || '[Candidate Address]'}<br>
                Email: ${offerData.candidateEmail}
              </div>
            </div>

            <div class="section">
              <p>Dear <strong>${offerData.candidateName}</strong>,</p>
              <p>We are delighted to offer you the position of <strong>${offerData.position}</strong> in the <strong>${offerData.department}</strong> department at <strong>${companyData.companyName || 'Our Company'}</strong>, effective from <strong>${formattedDateWithSuffix}</strong>.</p>
            </div>

            <div class="section">
              <div class="section-title">
                <span class="section-number">1</span>
                EMPLOYMENT DETAILS
              </div>
              <table class="compensation-table">
                <tr><td>Employee Code</td><td>${offerData.employeeCode}</td></tr>
                <tr><td>Designation</td><td>${offerData.position}</td></tr>
                <tr><td>Department</td><td>${offerData.department}</td></tr>
                <tr><td>Work Location</td><td>${offerData.workLocation || '[Location]'}</td></tr>
                <tr><td>Joining Date</td><td>${formattedDateWithSuffix}</td></tr>
                <tr><td>Reporting Manager</td><td>${offerData.reportingManager || '[Manager]'}</td></tr>
                <tr><td>Probation Period</td><td>${offerData.probationPeriod}</td></tr>
                <tr><td>Notice Period</td><td>${offerData.noticePeriod}</td></tr>
              </table>
            </div>

            <div class="section">
              <div class="section-title">
                <span class="section-number">2</span>
                COMPENSATION & BENEFITS
              </div>
              <table class="compensation-table">
                <tr><td>Component</td><td>Details</td></tr>
                <tr><td>Monthly Gross Salary</td><td><strong>${offerData.salary || '[Salary]'}</strong></td></tr>
                <tr><td>Annual CTC</td><td><strong>${offerData.ctc || '[CTC]'}</strong></td></tr>
                ${offerData.basicSalary ? `<tr><td>Basic Salary</td><td>${offerData.basicSalary}</td></tr>` : ''}
                ${offerData.hra ? `<tr><td>House Rent Allowance</td><td>${offerData.hra}</td></tr>` : ''}
                <tr><td>Provident Fund</td><td>${offerData.benefits?.pfContribution || 'As per statutory norms'}</td></tr>
                <tr><td>ESI Contribution</td><td>${offerData.benefits?.esiContribution || 'Applicable if eligible'}</td></tr>
                <tr><td>Medical Coverage</td><td>${offerData.benefits?.medicalCoverage || 'As per company policy'}</td></tr>
              </table>
            </div>

            <div class="section">
              <div class="section-title">
                <span class="section-number">3</span>
                WORK SCHEDULE & LEAVE POLICY
              </div>
              <table class="compensation-table">
                <tr><td>Policy Type</td><td>Details</td></tr>
                <tr><td>Working Hours</td><td>${offerData.workingHours}</td></tr>
                <tr><td>Saturday Schedule</td><td>${offerData.saturdayHours}</td></tr>
                <tr><td>Sick Leave</td><td>${offerData.leavePolicy?.sickLeave}</td></tr>
                <tr><td>Earned Leave</td><td>${offerData.leavePolicy?.earnedLeave}</td></tr>
                <tr><td>Casual Leave</td><td>${offerData.leavePolicy?.casualLeave || '6 days/year'}</td></tr>
              </table>
            </div>

            <div class="candidate-signature-note">
              <strong><FiAlertTriangle /> IMPORTANT INSTRUCTIONS</strong>
              <p>1. Please sign this appointment letter in the designated space below</p>
              <p>2. Return the signed copy via email to <strong>${companyData.companyEmail || 'HR Department'}</strong> within <strong>${companyData.offerValidityDays || 15} days</strong></p>
              <p>3. This offer is valid until: <strong>${new Date(offerData.offerExpiryDate).toLocaleDateString('en-IN')}</strong></p>
            </div>

            <div class="signature-area">
              <div class="signature-box">
                <p>FOR ${companyData.companyName || 'OUR COMPANY'}</p>
                ${hrSignature ? `
                  <img src="${hrSignature}" alt="HR Signature" class="signature-image">
                  <div class="digital-stamp">✓ Digitally Signed</div>
                ` : `
                  <div class="signature-line"></div>
                `}
                <div class="signature-details">
                  <strong>${companyData.authorizedSignatory || companyData.hrManagerName || 'Authorized Signatory'}</strong><br>
                  ${companyData.signatoryDesignation || 'HR Manager'}<br>
                  ${companyData.companyName || 'Our Company'}
                </div>
              </div>

              <div class="signature-box">
                <p>ACCEPTED BY CANDIDATE</p>
                <div class="signature-line"></div>
                <div class="digital-stamp">Candidate's Signature Required</div>
                <div class="signature-details">
                  <strong>${offerData.candidateName}</strong><br>
                  Signature: ___________________<br>
                  Date: ___________________<br>
                  Place: ___________________
                </div>
              </div>
            </div>

            <div class="document-footer">
              <p>This is a system-generated document. Any alteration or modification will render this document invalid.</p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // Send Offer Letter
  const sendOfferLetter = async () => {
    if (!offerData.joiningDate || !offerData.salary || !offerData.workLocation) {
      alert('⚠️ Please fill all required fields (Joining Date, Salary, Work Location)');
      return;
    }

    if (!companyData.companyName) {
      alert('⚠️ Please enter Company Name');
      return;
    }

    if (!hrSignature) {
      alert('⚠️ Please add HR signature before sending');
      return;
    }

    setIsSending(true);
    
    try {
      const userData = getUserDataFromToken();
      const token = sessionStorage.getItem('authToken') || sessionStorage.getItem('token');
      
      if (!userData) {
        alert('❌ Unable to identify current user. Please login again.');
        setIsSending(false);
        return;
      }

      const requestData = {
        applicationId: application._id,
        candidateEmail: application.email,
        candidateName: application.name,
        candidateAddress: application.address || 'Address not provided',
        position: application.jobTitle,
        department: application.department,
        offerData: {
          ...offerData,
          candidateAddress: application.address || 'Address not provided',
          benefits: offerData.benefits,
          leavePolicy: offerData.leavePolicy
        },
        companyData: {
          ...companyData,
          companyName: companyData.companyName || userData.company,
          companyEmail: companyData.companyEmail || userData.email,
          hrManagerName: companyData.hrManagerName || userData.name,
          authorizedSignatory: companyData.authorizedSignatory || userData.name,
          appointmentLetterNumber: companyData.appointmentLetterNumber || `AL/${new Date().getFullYear()}/${Math.floor(Math.random() * 1000).toString().padStart(4, '0')}`
        },
        hrSignature: hrSignature,
        companyStamp: companyStampPreview,
        userData: {
          ...userData,
          userId: userData.userId,
          email: userData.email
        }
      };

      console.log('📤 Sending offer letter for user:', userData.email);

      const response = await fetch(`${API_BASE_URL}/api/offer-letters/send-offer-letter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success('✅ Offer letter sent successfully to candidate!');
          onSave && onSave();
          onClose();
        } else {
          alert(`❌ Failed to send offer letter: ${result.message || 'Unknown error'}`);
        }
      } else {
        const errorData = await response.json();
        alert(`❌ Failed to send offer letter: ${errorData.message || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('Error sending offer letter:', error);
      alert('❌ Error sending offer letter. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Template Manager Modal
  const TemplateManagerModal = () => {
    const getCurrentTemplates = () => {
      if (activeTemplateSection.includes('.')) {
        const [parent, child] = activeTemplateSection.split('.');
        return templates[parent][child];
      }
      return templates[activeTemplateSection];
    };

    const currentTemplates = getCurrentTemplates();

    return (
      <div className="modal-overlay">
        <div className="modal-content template-manager-modal">
          <div className="modal-header">
            <h3><FiFileText /> Template Manager</h3>
            <button className="close-btn" onClick={() => setShowTemplateManager(false)}>X</button>
          </div>
          
          <div className="modal-body">
            <div className="template-section-selector">
              <h4>Select Section:</h4>
              <select 
                value={activeTemplateSection}
                onChange={(e) => setActiveTemplateSection(e.target.value)}
              >
                <optgroup label="Working Hours">
                  <option value="workingHours">Working Hours</option>
                  <option value="saturdayHours">Saturday Hours</option>
                </optgroup>
                <optgroup label="Leave Policy">
                  <option value="leavePolicy.sickLeave">Sick Leave</option>
                  <option value="leavePolicy.earnedLeave">Earned Leave</option>
                  <option value="leavePolicy.casualLeave">Casual Leave</option>
                  <option value="leavePolicy.maternityLeave">Maternity Leave</option>
                  <option value="leavePolicy.paternityLeave">Paternity Leave</option>
                </optgroup>
                <optgroup label="Benefits">
                  <option value="benefits.pfContribution">PF Contribution</option>
                  <option value="benefits.esiContribution">ESI Contribution</option>
                  <option value="benefits.medicalCoverage">Medical Coverage</option>
                </optgroup>
              </select>
            </div>
            
            <div className="existing-templates">
              <h4>Existing Templates:</h4>
              <div className="template-list">
                {currentTemplates.map(template => (
                  <div key={template.id} className={`template-item ${template.default ? 'default' : ''}`}>
                    <div className="template-header">
                      <span className="template-name">{template.name}</span>
                      {template.default && <span className="default-badge">Default</span>}
                    </div>
                    <div className="template-value">{template.value}</div>
                    <div className="template-actions">
                      <button
                        className="set-default-btn"
                        onClick={() => setAsDefaultTemplate(activeTemplateSection, template.id)}
                        disabled={template.default}
                      >
                        Set as Default
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => deleteTemplate(activeTemplateSection, template.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="add-template-form">
              <h4>Add New Template:</h4>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Template Name"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <textarea
                  placeholder="Template Value/Content"
                  value={newTemplate.value}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, value: e.target.value }))}
                  rows="3"
                />
              </div>
              <button className="add-btn" onClick={addNewTemplate}>
                Add Template
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Terms Template Manager Modal
  const TermsTemplateManager = () => (
    <div className="modal-overlay">
      <div className="modal-content terms-manager-modal">
        <div className="modal-header">
          <h3><FiEdit /> Terms & Conditions Templates</h3>
          <button className="close-btn" onClick={() => setShowTermsManager(false)}>X</button>
        </div>
        
        <div className="modal-body">
          <div className="template-selector-section">
            <h4>Select Template:</h4>
            <div className="terms-template-list">
              {termsTemplates.map(template => (
                <div key={template.id} className={`terms-template-item ${offerData.selectedTemplate === template.name ? 'active' : ''} ${template.default ? 'default' : ''}`}>
                  <div className="template-header">
                    <span className="template-name">{template.name}</span>
                    {template.default && <span className="default-badge">Default</span>}
                  </div>
                  <div className="template-preview">
                    {template.content.split('\n').slice(0, 3).join(' ')}...
                  </div>
                  <div className="template-actions">
                    <button
                      className="use-btn"
                      onClick={() => handleTermsTemplateSelect(template)}
                    >
                      Use
                    </button>
                    <button
                      className="set-default-btn"
                      onClick={() => setAsDefaultTermsTemplate(template.id)}
                      disabled={template.default}
                    >
                      Set Default
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => deleteTermsTemplate(template.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="custom-terms-section">
            <h4>Custom Terms:</h4>
            <textarea
              value={offerData.termsAndConditions}
              onChange={(e) => handleCustomTermsChange(e.target.value)}
              placeholder="Enter custom terms and conditions..."
              rows="10"
            />
            <div className="terms-actions">
              <button
                className="save-template-btn"
                onClick={addNewTermsTemplate}
                disabled={!offerData.termsAndConditions.trim()}
              >
                Save as New Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Form validation
  const isFormValid = offerData.joiningDate && 
    offerData.salary && 
    offerData.workLocation && 
    companyData.companyName && 
    hrSignature;

  return (
    <div className="modal-overlay">
      <div className="modal-content offer-letter-modal">
        <div className="modal-header">
          <h2><FiFileText />Generate & Send Appointment Letter</h2>
          <button className="close-btn" onClick={onClose}>X</button>
        </div>

        <div className="modal-body">
          {/* HR Signature & Company Stamp Section */}
          <div className="signatures-section">
            <h3><FiEdit /> Digital Signatures & Stamps (Required)</h3>
            <div className="signatures-grid">
              <div className="signature-item">
                <h4>HR Signature</h4>
                {hrSignature ? (
                  <div className="signature-preview">
                    <img src={hrSignature} alt="HR Signature" />
                    <button 
                      className="change-signature-btn"
                      onClick={openSignaturePad}
                    >
                      Change Signature
                    </button>
                    <p className="signature-status"><FiCheckCircle /> HR Signature Added</p>
                  </div>
                ) : (
                  <div className="signature-required">
                    <button 
                      className="add-signature-btn"
                      onClick={openSignaturePad}
                    >
                      <FiEdit /> Add HR Signature
                    </button>
                    <p className="signature-note">Required before sending</p>
                  </div>
                )}
              </div>
              
              <div className="signature-item">
                <h4>Company Stamp</h4>
                {companyStampPreview ? (
                  <div className="signature-preview">
                    <img src={companyStampPreview} alt="Company Stamp" />
                    <input
                      type="file"
                      id="stamp-upload"
                      accept="image/*"
                      onChange={handleStampUpload}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="stamp-upload" className="change-signature-btn">
                      Change Stamp
                    </label>
                  </div>
                ) : (
                  <div className="signature-required">
                    <input
                      type="file"
                      id="stamp-upload"
                      accept="image/*"
                      onChange={handleStampUpload}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="stamp-upload" className="add-signature-btn">
                      <FiHome /> Add Company Stamp
                    </label>
                    <p className="signature-note">Optional</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Company Information Section */}
          <div className="company-info-section">
            <h3><FiHome /> Company Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  placeholder="Enter company name"
                  value={companyData.companyName}
                  onChange={(e) => handleCompanyInputChange('companyName', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Authorized Signatory *</label>
                <input
                  type="text"
                  placeholder="Name of signatory"
                  value={companyData.authorizedSignatory}
                  onChange={(e) => handleCompanyInputChange('authorizedSignatory', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Designation *</label>
                <input
                  type="text"
                  placeholder="Designation"
                  value={companyData.signatoryDesignation}
                  onChange={(e) => handleCompanyInputChange('signatoryDesignation', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Company Email *</label>
                <input
                  type="email"
                  placeholder="Email address"
                  value={companyData.companyEmail}
                  onChange={(e) => handleCompanyInputChange('companyEmail', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Company Phone *</label>
                <input
                  type="text"
                  placeholder="Phone number"
                  value={companyData.companyPhone}
                  onChange={(e) => handleCompanyInputChange('companyPhone', e.target.value)}
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Registered Office Address *</label>
                <textarea
                  placeholder="Complete registered office address"
                  value={companyData.registeredOffice}
                  onChange={(e) => handleCompanyInputChange('registeredOffice', e.target.value)}
                  rows="3"
                  required
                />
              </div>
            </div>
          </div>

          {/* Offer Details Section */}
          <div className="offer-details-section">
            <h3><FiBriefcase /> Offer Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Joining Date *</label>
                <input
                  type="date"
                  value={offerData.joiningDate}
                  onChange={(e) => handleInputChange('joiningDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label>Monthly Gross Salary *</label>
                <input
                  type="text"
                  placeholder="e.g., ₹90,000 per month"
                  value={offerData.salary}
                  onChange={(e) => handleInputChange('salary', e.target.value)}
                  required
                />
                <button 
                  className="calculate-btn"
                  onClick={calculateSalaryBreakup}
                  style={{marginTop: '5px', padding: '3px 8px', fontSize: '12px'}}
                >
                  Calculate Salary Breakup
                </button>
              </div>

              <div className="form-group">
                <label>Annual CTC</label>
                <input
                  type="text"
                  value={offerData.ctc}
                  onChange={(e) => handleInputChange('ctc', e.target.value)}
                  placeholder="Auto-calculated"
                  readOnly
                />
              </div>

              <div className="form-group">
                <label>Work Location *</label>
                <input
                  type="text"
                  placeholder="e.g., Bangalore, Karnataka"
                  value={offerData.workLocation}
                  onChange={(e) => handleInputChange('workLocation', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Salary Breakdown Section */}
            <div className="salary-breakup-section">
              <h4>Salary Breakup (Optional)</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Basic Salary</label>
                  <input
                    type="text"
                    placeholder="Basic"
                    value={offerData.basicSalary}
                    onChange={(e) => handleInputChange('basicSalary', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>HRA</label>
                  <input
                    type="text"
                    placeholder="House Rent Allowance"
                    value={offerData.hra}
                    onChange={(e) => handleInputChange('hra', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Working Schedule Section */}
          <div className="working-schedule-section">
            <h3><FiClock /> Working Schedule</h3>
            <div className="form-grid">
              <div className="form-group">
                <TemplateSelector
                  label="Working Hours"
                  value={offerData.workingHours}
                  section="workingHours"
                  options={templates.workingHours}
                />
              </div>
              
              <div className="form-group">
                <TemplateSelector
                  label="Saturday Schedule"
                  value={offerData.saturdayHours}
                  section="saturdayHours"
                  options={templates.saturdayHours}
                />
              </div>
            </div>
          </div>

          {/* Leave Policy Section */}
          <div className="leave-policy-section">
            <h3><FiCalendar /> Leave Policy</h3>
            <div className="form-grid">
              <div className="form-group">
                <TemplateSelector
                  label="Sick Leave"
                  value={offerData.leavePolicy.sickLeave}
                  section="leavePolicy"
                  subSection="sickLeave"
                  options={templates.leavePolicy.sickLeave}
                />
              </div>
              
              <div className="form-group">
                <TemplateSelector
                  label="Earned Leave"
                  value={offerData.leavePolicy.earnedLeave}
                  section="leavePolicy"
                  subSection="earnedLeave"
                  options={templates.leavePolicy.earnedLeave}
                />
              </div>
              
              <div className="form-group">
                <TemplateSelector
                  label="Casual Leave"
                  value={offerData.leavePolicy.casualLeave}
                  section="leavePolicy"
                  subSection="casualLeave"
                  options={templates.leavePolicy.casualLeave}
                />
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="benefits-section">
            <h3><FiDollarSign /> Benefits & Perks</h3>
            <div className="form-grid">
              <div className="form-group">
                <TemplateSelector
                  label="PF Contribution"
                  value={offerData.benefits.pfContribution}
                  section="benefits"
                  subSection="pfContribution"
                  options={templates.benefits.pfContribution}
                />
              </div>
              
              <div className="form-group">
                <TemplateSelector
                  label="ESI Contribution"
                  value={offerData.benefits.esiContribution}
                  section="benefits"
                  subSection="esiContribution"
                  options={templates.benefits.esiContribution}
                />
              </div>
              
              <div className="form-group">
                <TemplateSelector
                  label="Medical Coverage"
                  value={offerData.benefits.medicalCoverage}
                  section="benefits"
                  subSection="medicalCoverage"
                  options={templates.benefits.medicalCoverage}
                />
              </div>
            </div>
          </div>

          {/* Terms & Conditions Section */}
          <div className="terms-section">
            <h3><FiClipboard /> Terms & Conditions</h3>
            <div className="terms-header">
              <div className="template-selector">
                <label>Select Template:</label>
                <div className="template-dropdown">
                  <select
                    value={offerData.selectedTemplate}
                    onChange={(e) => {
                      const template = termsTemplates.find(t => t.name === e.target.value);
                      if (template) {
                        handleTermsTemplateSelect(template);
                      } else if (e.target.value === 'Custom') {
                        setCustomTerms(offerData.termsAndConditions);
                      }
                    }}
                  >
                    {termsTemplates.map(template => (
                      <option key={template.id} value={template.name}>
                        {template.name}
                      </option>
                    ))}
                    <option value="Custom">Custom</option>
                  </select>
                  <button
                    className="edit-template-btn"
                    onClick={() => setShowTermsManager(true)}
                    title="Manage Terms Templates"
                  >
                    <FiSettings />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="form-group full-width">
              <textarea
                value={offerData.termsAndConditions}
                onChange={(e) => handleCustomTermsChange(e.target.value)}
                placeholder="Enter terms and conditions..."
                rows="6"
              />
            </div>
          </div>

          {/* Template Management Button */}
          <div className="template-management-section">
            <button
              className="template-manager-btn"
              onClick={() => setShowTemplateManager(true)}
            >
              <FiSettings /> Manage All Templates
            </button>
          </div>

          {/* Process Information */}
          <div className="process-info">
            <h4><FiClipboard /> Process Flow:</h4>
            <ol>
              <li><FiCheckCircle /> HR fills offer details and signs digitally</li>
              <li><FiMail /> System sends email to candidate with download link</li>
              <li><FiDownload /> Candidate downloads PDF, reviews and signs</li>
              <li><FiUpload /> Candidate returns signed copy via email/portal</li>
              <li><FiCheckCircle /> Offer letter process completed</li>
            </ol>
          </div>
        </div>

        <div className="modal-footer">
          <button className="secondary-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
  className="secondary-btn" 
  onClick={generatePDF}
  disabled={isGeneratingPDF}
>
  {isGeneratingPDF ? (
    <>
      <FiClock /> Generating...
    </>
  ) : (
    <>
      <FiEye /> Preview PDF
    </>
  )}
</button>

         <button 
  className="primary-btn" 
  onClick={sendOfferLetter}
  disabled={!isFormValid || isSending}
>
  {isSending ? (
    <>
      <FiMail /> Sending...
    </>
  ) : (
    <>
      <FiSend /> Send to Candidate
    </>
  )}
</button>

        </div>
      </div>

      {/* Digital Signature Modal */}
      {showSignatureModal && (
        <div className="modal-overlay">
          <div className="modal-content signature-modal">
            <div className="modal-header">
              <h3><FiEdit /> Add Digital Signature</h3>
              <button className="close-btn" onClick={() => setShowSignatureModal(false)}>X</button>
            </div>
            
            <div className="modal-body">
              <div className="signature-instructions">
                <p>Please sign in the box below using your mouse or touchpad:</p>
              </div>
              
              <div className="signature-canvas-container">
                <canvas
                  ref={signatureCanvasRef}
                  className="signature-canvas"
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    handleCanvasMouseDown(touch);
                  }}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    handleCanvasMouseMove(touch);
                  }}
                  onTouchEnd={handleCanvasMouseUp}
                />
              </div>
              
              <div className="signature-actions">
                <button className="secondary-btn" onClick={clearSignature}>
                  <FiTrash2 /> Clear Signature
                </button>
                <button className="primary-btn" onClick={handleSaveSignature}>
                  <FiSave /> Save Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Manager Modal */}
      {showTemplateManager && <TemplateManagerModal />}
      
      {/* Terms Template Manager Modal */}
      {showTermsManager && <TermsTemplateManager />}
    </div>
  );
};

export default OfferLetter;